import React, { useCallback, useState } from "react";

import { Divider, Grid } from "@material-ui/core";
import { useDispatch, useSelector } from "react-redux";
import { v4 as uuidv4 } from "uuid";

import { addSaleLotWithPens, setSetting } from "actions";

import { SecondaryButton } from "components/Form";
import { Button } from "components/Form/Button";
import { CheckBoxBase } from "components/Form/FormikControls";
import HideOnScrollContainer from "components/Layout/HideOnScroll";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from "components/MaterialDialog";
import { InlineCreateSaleLotComponent } from "components/SaleLotForms/InlineCreateSaleLotForm";

import { PenTypes } from "constants/auctionPens";
import { ExportSite } from "constants/exportSites";
import { SaleLotModalSection } from "constants/navigation";
import {
  DeploymentPermission,
  SaleyardPermission,
} from "constants/permissions";
import { Settings } from "constants/settings";

import {
  closeAllHashModals,
  getInlineCreateSaleLotModalRoute,
  openEditSaleLotModal,
  openInlineCreateSaleLotModal,
  openScanModal,
  returnToLocation,
} from "lib/navigation";
import { isUUID } from "lib/uuid";

import {
  getCurrentSaleyard,
  getDeploymentSaleIdByConsignmentId,
  getExportSitesByDeploymentSaleId,
  getSetting,
  selectRoleDeployments,
} from "selectors";

import {
  useHasPermission,
  useIsMobile,
  useSomeHasPermission,
  useToggle,
} from "hooks";

export interface InlineCreateSaleLotOptions {
  autoFocusAuctionPen?: boolean;
  autoPopulateNextPen?: boolean;
  ignoreHeadCountForVendorSearch?: boolean;
  includeCreateConsignmentButton?: boolean;
  includeCreateUnknownConsignment?: boolean;
  includeStoredValues?: boolean;
  newId?: string;
  showAuctionPenAtTop?: boolean;
  showDeliveryPen?: boolean;
  showPenQuickButtons?: boolean;
}

export interface InlineCreateSaleLotInitialValues extends Partial<SaleLot> {
  // Legacy form-only field - not part of the SaleLot model.
  description_id?: number | null;
}

// The vendor business can be unknown, in which case consignment_id holds an
// agencyId (number) rather than a consignment uuid (string).
interface InlineCreateSaleLotFormValues
  extends Omit<InlineCreateSaleLotInitialValues, "consignment_id"> {
  consignment_id: string | number;
}

interface FooterActionsCommonProps {
  allowRememberDraftingAttributes: boolean;
  closeSelf: () => void;
  onChangeRememberSettings: () => void;
  options: InlineCreateSaleLotOptions;
  rememberDraftingAttributes: boolean;
  returnTo?: string;
  saveDisabled: boolean;
  values: InlineCreateSaleLotFormValues | null;
}

interface AfterCreateOptions {
  detailedModalSection?: string | null;
  openDetailedModal?: boolean;
  openScanModal?: boolean;
  rememberDraftingAttributes?: boolean;
  resetForm?: boolean;
}

const FooterActions = ({
  commonProps,
}: {
  commonProps: FooterActionsCommonProps;
}) => {
  const {
    values,
    closeSelf,
    saveDisabled,
    allowRememberDraftingAttributes,
    rememberDraftingAttributes,
    onChangeRememberSettings,
    returnTo,
    options,
  } = commonProps;

  const dispatch = useDispatch();
  const deploymentSaleId = useSelector(
    getDeploymentSaleIdByConsignmentId(values?.consignment_id),
  );

  const exportSites: ExportSite[] = useSelector(
    getExportSitesByDeploymentSaleId(deploymentSaleId),
  );

  const hasAuctionsPlusEnabled = exportSites?.includes(
    ExportSite.AUCTIONS_PLUS,
  );

  const hasStockLiveEnabled = exportSites?.includes(ExportSite.STOCK_LIVE);

  function handleCreateSaleLot(
    afterCreateOptions: AfterCreateOptions = {
      openScanModal: false,
      resetForm: false,
      openDetailedModal: false,
      detailedModalSection: null,
      rememberDraftingAttributes: false,
    },
  ) {
    const { newId = null } = options;

    const newSaleLotId = newId || uuidv4();
    dispatch(addSaleLotWithPens(newSaleLotId, values as Partial<SaleLot>));

    if (rememberDraftingAttributes) {
      dispatch(setSetting(Settings.storedSaleLotFormData, values));
    } else {
      dispatch(setSetting(Settings.storedSaleLotFormData, null));
    }

    // ! When vendor business is unknown the agencyId (number) is passed into consignmentId
    const vendorIsKnown = isUUID(values.consignment_id);

    closeSelf();
    if (afterCreateOptions.resetForm) {
      // Re-open self, but force a reset of the form with the timeout - otherwise
      // it just remounts with the same data.
      setTimeout(() => {
        openInlineCreateSaleLotModal(
          { sale_round_id: values.sale_round_id },
          options,
          returnTo,
        );
      }, 0);
    } else if (afterCreateOptions.openScanModal) {
      const newInlineSaleLotModalRoute = `#${getInlineCreateSaleLotModalRoute(
        { sale_round_id: values.sale_round_id },
        options,
        returnTo,
      )}`;
      openScanModal(
        vendorIsKnown ? values.consignment_id : null,
        newSaleLotId,
        false,
        null,
        PenTypes.SELLING,
        newInlineSaleLotModalRoute,
      );
    } else if (afterCreateOptions.openDetailedModal) {
      openEditSaleLotModal(
        newSaleLotId,
        afterCreateOptions.detailedModalSection,
      );
    }
  }

  function handleCreateSaleLotAndScan() {
    handleCreateSaleLot({ openScanModal: true, rememberDraftingAttributes });
  }

  function handleCreateSaleLotAndAssess() {
    handleCreateSaleLot({
      openDetailedModal: true,
      detailedModalSection: SaleLotModalSection.INTERFACES,
      rememberDraftingAttributes,
    });
  }

  function handleCreateSaleLotAndNew() {
    handleCreateSaleLot({ resetForm: true, rememberDraftingAttributes });
  }
  return (
    <>
      <DialogActions shrink={0}>
        <SecondaryButton data-tour="cancel" onClick={closeSelf}>
          Cancel
        </SecondaryButton>
        <Button
          data-tour="saveAndClose"
          disabled={saveDisabled}
          onClick={handleCreateSaleLot}
        >
          Save &amp; Close
        </Button>

        <Button
          data-tour="saveAndScan"
          disabled={saveDisabled}
          onClick={handleCreateSaleLotAndScan}
        >
          Save &amp; Scan
        </Button>
        {(hasAuctionsPlusEnabled || hasStockLiveEnabled) && (
          <Button
            data-tour="saveAndAssess"
            disabled={saveDisabled}
            onClick={handleCreateSaleLotAndAssess}
          >
            Save &amp; Assess
          </Button>
        )}
        <Button
          data-tour="saveAndNew"
          disabled={saveDisabled}
          onClick={handleCreateSaleLotAndNew}
        >
          Save &amp; New
        </Button>
      </DialogActions>
      {allowRememberDraftingAttributes && (
        <>
          <Divider />
          <DialogActions>
            <Grid item xs={12}>
              <CheckBoxBase
                label="Remember Drafting Attributes"
                onChange={onChangeRememberSettings}
                value={rememberDraftingAttributes}
              />
            </Grid>
          </DialogActions>
        </>
      )}
    </>
  );
};

const FooterActionsComponent = React.memo(FooterActions);

interface InlineCreateSaleLotModalProps {
  initialValues?: InlineCreateSaleLotInitialValues;
  options: InlineCreateSaleLotOptions;
  returnTo?: string;
}

export function InlineCreateSaleLotModal({
  returnTo,
  initialValues = {},
  options,
}: InlineCreateSaleLotModalProps) {
  const isMobile = useIsMobile();

  const [modalPortalTarget, setModalPortalTarget] =
    useState<HTMLElement | null>(null);
  const setContentRef = useCallback((node: HTMLElement | null) => {
    if (node) {
      setModalPortalTarget(node);
    }
  }, []);
  const storedSaleLotFormValues = useSelector(
    getSetting(Settings.storedSaleLotFormData),
  );

  const { includeStoredValues = false } = options;

  const hasDeploymentAllowRememberLastDraftingAttributesPermission =
    useSomeHasPermission(
      selectRoleDeployments,
      DeploymentPermission.featureAllowRememberLastDraftingAttributes,
    );

  const hasSaleYardAllowRememberLastDraftingAttributesPermission =
    useHasPermission(
      getCurrentSaleyard,
      SaleyardPermission.featureAllowRememberLastDraftingAttributes,
    );

  const allowRememberDraftingAttributes =
    includeStoredValues &&
    (hasDeploymentAllowRememberLastDraftingAttributesPermission ||
      hasSaleYardAllowRememberLastDraftingAttributesPermission);

  const [isValid, setIsValid] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const [values, setValues] = useState<InlineCreateSaleLotFormValues | null>(
    null,
  );

  const [rememberDraftingAttributes, toggleRememberDraftingAttributes] =
    useToggle(includeStoredValues && storedSaleLotFormValues);

  const onChangeRememberSettings = () => toggleRememberDraftingAttributes();

  const closeSelf = () => {
    if (returnTo) {
      returnToLocation(returnTo);
    } else {
      closeAllHashModals();
    }
  };

  const contextUpdateSaleLot = useCallback(
    (
      newValues: InlineCreateSaleLotFormValues | null,
      newIsValid = false,
      newIsDirty = true,
    ) => {
      setIsValid(newIsValid);
      setIsDirty(newIsDirty);
      setValues(newValues);
    },
    [setIsValid, setIsDirty, setValues],
  );

  const saveDisabled = !isValid || !isDirty;

  if (rememberDraftingAttributes && storedSaleLotFormValues) {
    initialValues.breed_id = storedSaleLotFormValues.breed_id;
    initialValues.sex_id = storedSaleLotFormValues.sex_id;
    initialValues.age_id = storedSaleLotFormValues.age_id;
    initialValues.description_id = storedSaleLotFormValues.description_id;
  }

  const footerActionsCommonProps = {
    values,
    closeSelf,
    saveDisabled,
    allowRememberDraftingAttributes,
    rememberDraftingAttributes,
    onChangeRememberSettings,
    returnTo,
    options,
  };

  return (
    <Dialog open fullScreen={isMobile} onClose={closeSelf}>
      <HideOnScrollContainer target={modalPortalTarget} allowHide={isMobile}>
        <DialogTitle onClose={closeSelf}>Create New Sale Lot</DialogTitle>
      </HideOnScrollContainer>
      <DialogContent dividers ref={setContentRef}>
        <InlineCreateSaleLotComponent
          contextUpdateSaleLot={contextUpdateSaleLot}
          initialValues={initialValues}
          options={{
            ...options,
            autoFocusAuctionPen: true,
            ignoreHeadCountForVendorSearch: true,
          }}
          menuPortalTarget={modalPortalTarget}
        />
        {/* Place actions in the content requiring scroll for mobile */}
        {isMobile && (
          <FooterActionsComponent commonProps={footerActionsCommonProps} />
        )}
      </DialogContent>
      {/* Place actions outside of the content not requiring scroll for desktop */}
      {!isMobile && (
        <FooterActionsComponent commonProps={footerActionsCommonProps} />
      )}
    </Dialog>
  );
}
